import { FirebaseAuthentication } from '@capacitor-firebase/authentication';
import {
  IonBackButton,
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonInput,
  IonInputPasswordToggle,
  IonPage,
  IonRouterLink,
  IonSpinner,
  IonTitle,
  IonToolbar,
  useIonToast
} from '@ionic/react';
import { motion } from 'framer-motion';
import { arrowBack } from 'ionicons/icons';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';

const CustomSignUp: React.FC = () => {
  const { t } = useTranslation(['Auth', 'MGP']);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordFocused, setPasswordFocused] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isEmailSent, setIsEmailSent] = useState(false);
  const history = useHistory();
  const [present] = useIonToast();

  const validateEmail = (email: string) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  // Password validation rules
  const hasMinLength = (password: string) => password.length >= 8;
  const hasUpperCase = (password: string) => /[A-Z]/.test(password);
  const hasLowerCase = (password: string) => /[a-z]/.test(password);
  const hasSpecialChar = (password: string) => /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password);
  const hasNumber = (password: string) => /\d/.test(password);

  // Check if passwords match
  const passwordsMatch = (password: string, confirmPassword: string) => {
    return password === confirmPassword && password.length > 0;
  };

  // Comprehensive password validation
  const validatePassword = (password: string) => {
    return hasMinLength(password) &&
      hasUpperCase(password) &&
      hasLowerCase(password) &&
      hasSpecialChar(password) &&
      hasNumber(password);
  };

  // Check if all validation rules are met to enable the signup button
  const isFormValid = () => {
    return validateEmail(email) &&
      validatePassword(password) &&
      passwordsMatch(password, confirmPassword);
  };

  const handleSubmit = async () => {
    setError('');

    // Validation
    if (!validateEmail(email)) {
      setError(t('Auth:invalid_email'));
      return;
    }

    if (!validatePassword(password)) {
      setError(t('Auth:password_requirements_not_met'));
      return;
    }

    if (password !== confirmPassword) {
      setError(t('Auth:passwords_dont_match'));
      return;
    }

    setIsLoading(true);

    try {
      // Create user with email and password
      const result = await FirebaseAuthentication.createUserWithEmailAndPassword({
        email,
        password
      });

      // Send verification email
      if (result.user) {
        await FirebaseAuthentication.sendEmailVerification();
        setIsEmailSent(true);
        present({
          message: t('Auth:verification_email_sent'),
          duration: 3000,
          position: 'bottom',
          color: 'success'
        });
      }
    } catch (err: any) {
      console.error('Error:', err);
      const errorCode = (err.code as string).replace('auth/', '');
      const errorMessage = t(`Auth:${errorCode}`) as string;
      setError(errorMessage || err.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar className="md:hidden px-2">
          <IonButtons slot="start">
            <IonBackButton icon={arrowBack} color="medium" text="" defaultHref='/mobile' />
          </IonButtons>
          <IonTitle>{t('Auth:sign_up')}</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent scrollY={false}>
        <div className="flex flex-col p-3 justify-center bg-background items-center h-full md:bg-[url('/assets/images/bg/bg3.webp')] bg-cover bg-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="flex flex-col p-3 justify-center rounded-lg items-center"
          >
            <div className="w-full max-w-md md:bg-card md:p-8 rounded-xl md:mt-[-64px]">
              {isEmailSent ? (
                <div className="text-center space-y-4">
                  <h2 className="text-xl font-semibold">{t('Auth:check_your_email')}</h2>
                  <p className="text-muted-foreground">
                    {t('Auth:verification_email_instructions')}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {t('Auth:email_sent_to')} {email}
                  </p>
                  <IonButton
                    expand="block"
                    shape="round"
                    onClick={() => history.push('/sign-in')}
                  >
                    {t('Auth:continue_to_sign_in')}
                  </IonButton>
                </div>
              ) : (
                <div className="space-y-4">
                  <h2 className="text-2xl md:block hidden font-semibold text-foreground">{t('Auth:sign_up')}</h2>
                  <div className="space-y-4">
                    <IonInput
                      type="email"
                      autocomplete='off'
                      value={email}
                      onIonInput={(e) => setEmail(e.detail.value!)}
                      placeholder={t('Auth:email')}
                      className="h-12 rounded-2xl bg-input border border-input text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary overflow-hidden"
                      style={{
                        '--padding-start': '1rem',
                        '--padding-end': '1rem',
                      }}
                    />
                    <IonInput
                      type="password"
                      autocomplete='off'
                      value={password}
                      onIonInput={(e) => setPassword(e.detail.value!)}
                      onIonFocus={() => setPasswordFocused(true)}
                      onIonBlur={() => setPasswordFocused(false)}
                      placeholder={t('Auth:password')}
                      className="h-12 rounded-2xl bg-input border border-input text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary overflow-hidden"
                      style={{
                        '--padding-start': '1rem',
                        '--padding-end': '1rem',
                      }}
                    >
                      <IonInputPasswordToggle color="medium" slot="end"></IonInputPasswordToggle>
                    </IonInput>

                    {/* Password validation checklist */}
                    <div className="px-2 mb-3 text-sm">
                      <ul className="space-y-1 grid grid-cols-2">
                        <li className={`flex items-center ${hasMinLength(password) ? 'text-green-500' : 'text-red-500'}`}>
                          <span className="mr-2">{hasMinLength(password) ? '✅' : '❌'}</span>
                          {t('Auth:password_min_length', 'Au moins 8 caractères')}
                        </li>
                        <li className={`flex items-center ${hasUpperCase(password) ? 'text-green-500' : 'text-red-500'}`}>
                          <span className="mr-2">{hasUpperCase(password) ? '✅' : '❌'}</span>
                          {t('Auth:password_uppercase', 'Majuscule requise')}
                        </li>
                        <li className={`flex items-center ${hasLowerCase(password) ? 'text-green-500' : 'text-red-500'}`}>
                          <span className="mr-2">{hasLowerCase(password) ? '✅' : '❌'}</span>
                          {t('Auth:password_lowercase', 'Minuscule requise')}
                        </li>
                        <li className={`flex items-center ${hasSpecialChar(password) ? 'text-green-500' : 'text-red-500'}`}>
                          <span className="mr-2">{hasSpecialChar(password) ? '✅' : '❌'}</span>
                          {t('Auth:password_special_char', 'Caractère spécial requis')}
                        </li>
                        <li className={`flex items-center ${hasNumber(password) ? 'text-green-500' : 'text-red-500'}`}>
                          <span className="mr-2">{hasNumber(password) ? '✅' : '❌'}</span>
                          {t('Auth:password_number', 'Chiffre requis')}
                        </li>
                      </ul>
                    </div>
                    <IonInput
                      type="password"
                      value={confirmPassword}
                      onIonInput={(e) => setConfirmPassword(e.detail.value!)}
                      placeholder={t('Auth:confirm_password')}
                      className="h-12 rounded-2xl bg-input border border-input text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary overflow-hidden"
                      style={{
                        '--padding-start': '1rem',
                        '--padding-end': '1rem',
                      }}
                    >
                      <IonInputPasswordToggle color="medium" slot="end"></IonInputPasswordToggle>
                    </IonInput>
                    {!passwordsMatch(password, confirmPassword) && (
                      <li className={`flex text-sm items-center ${passwordsMatch(password, confirmPassword) ? 'text-green-500' : 'text-red-500'}`}>
                        <span className="mr-2">{passwordsMatch(password, confirmPassword) ? '✅' : '❌'}</span>
                        {t('Auth:passwords_match', 'Les mots de passe correspondent')}
                      </li>
                    )}
                  </div>

                  {error && (
                    <p className="text-sm text-destructive">{error}</p>
                  )}

                  <IonButton
                    expand="block"
                    shape="round"
                    onClick={handleSubmit}
                    disabled={isLoading || !isFormValid()}
                    className={!isFormValid() ? 'opacity-50' : ''}
                  >
                    {isLoading ? (
                      <IonSpinner name="dots" />
                    ) : (
                      t('Auth:sign_up')
                    )}
                  </IonButton>

                  <div className="text-center text-sm">
                    <span className="text-muted-foreground">{t('Auth:already_have_account')} </span>
                    <IonRouterLink routerLink="/sign-in" routerDirection='back' className="text-foreground font-medium">
                      {t('Auth:sign_in')}
                    </IonRouterLink>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default CustomSignUp;
